import {
  BaseQuestionError,
  DisclosureErrorPayload,
  InterviewError,
} from "./types/InterviewError";
import {
  DeleteDisclosureRequest,
  DisclosureRequest,
} from "./types/DisclosureRequest";
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  appendCustomSections,
  getOrderedSectionIndex,
  getSectionsByPageCode,
  initializeInterviewPosition,
} from "./utils";
import {
  clearBaseQuestionError,
  clearDisclosureError,
  setBaseQuestionError,
  setDisclosureError,
} from "./updateUtils/updateInterviewErrors";
import {
  findLastActivePageAndSection,
  getIndexOfSection,
  getNextSectionForCurrent,
  getPreviousSectionForCurrent,
  updatePageAndFirstSectionByPageCode,
} from "./updateUtils/updateCurrent";
import {
  mapPhysicianResourceToEdhPrimaryPhysicianResource,
  updateDislosurePhysicianList,
} from "./updateUtils/updatePhysician";
import {
  mergeUpdatedDisclosures,
  removeDisclosureFromBaseQuestionByAliasName,
  removeEmbeddedDisclosureByAliasName,
} from "./updateUtils/updateDisclosures";

import { BaseQuestionResource } from "./types/BaseQuestionResource";
import { CurrentPageAndSection } from "./types/CurrentPageAndSection";
import { EdhConditionDisclosureResource } from "./types/EdhConditionDisclosureResource";
import { EdhQuestionPagesResource } from "./types/EdhQuestionPagesResource";
import { InterviewAnswerRequest } from "./types/InterviewAnswerRequest";
import { InterviewMetadata } from "./types/InterviewMetadata";
import { InterviewRequest } from "./types/InterviewRequest";
import { mergeUpdatedBaseQuestion } from "./updateUtils/updateBaseQuestion";
import { actions as physicianActions } from "../physician/slice";
import { requestSingleCaseSuccessful } from "../singleCase/slice";
import { updateBaseQuestionDisclosures } from "./updateUtils/updateBaseQuestionDisclosures";
import { updateMetadataValues } from "./updateUtils/updateMetadata";

export interface InterviewState {
  loading: boolean;
  questions: EdhQuestionPagesResource;
  current: CurrentPageAndSection;
  error: InterviewError;
  metadata: InterviewMetadata;
}

const initialState: InterviewState = {
  loading: false,
  questions: {
    _embedded: {
      disclosure_physicians: [],
      disclosures: [],
      links: [],
      lists: [],
      physicians: [],
      primaryPhysician: [],
    },
    _links: [],
    pages: [],
    caseId: "",
  },
  current: {
    page: {
      code: "",
      index: undefined,
    },
    section: {
      code: "",
      index: undefined,
    },
  },
  error: {
    interview: undefined,
    baseQuestions: [],
    disclosures: [],
  },
  metadata: {
    pagesCount: undefined,
    sectionsCount: undefined,
    orderedSectionIndex: undefined,
    risks: [],
  },
};

const interviewSlice = createSlice({
  name: "interview",
  initialState,
  reducers: {
    requestStartInterview: (state, action: PayloadAction<InterviewRequest>) => {
      state.loading = true;
      state.error.interview = undefined;
    },
    requestStartInterviewSuccessful: (
      state,
      action: PayloadAction<EdhQuestionPagesResource>,
    ) => {
      state.loading = false;
      state.questions = action.payload;

      state.questions.pages = appendCustomSections(
        state.questions.pages,
        state.metadata.risks,
      );

      state.current = initializeInterviewPosition(
        state.questions,
        state.current,
      );

      state.metadata = updateMetadataValues(
        state.current,
        state.metadata,
        state.questions,
      );
    },
    requestStartInterviewFailed: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error.interview = action.payload;
    },
    requestInterview: (state, action: PayloadAction<InterviewRequest>) => {
      state.loading = true;
      state.error.interview = undefined;
      state.current = initialState.current;
    },
    requestInterviewSuccessful: (
      state,
      action: PayloadAction<EdhQuestionPagesResource>,
    ) => {
      state.loading = false;
      state.questions = action.payload;

      // Add the primary physician section to page before the last one.
      state.questions.pages = appendCustomSections(
        state.questions.pages,
        state.metadata.risks,
      );

      state.current = initializeInterviewPosition(
        state.questions,
        state.current,
      );

      state.metadata = updateMetadataValues(
        state.current,
        state.metadata,
        state.questions,
      );
    },
    requestInterviewFailed: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error.interview = action.payload;
    },
    requestSubmitBaseQuestion: (
      state,
      action: PayloadAction<InterviewAnswerRequest>,
    ) => {
      const { code } = action.payload;
      state.error.baseQuestions = clearBaseQuestionError(
        state.error.baseQuestions,
        code,
      );
    },
    requestSubmitBaseQuestionSuccessful: (
      state,
      action: PayloadAction<BaseQuestionResource>,
    ) => {
      state.questions.pages = mergeUpdatedBaseQuestion(
        state.questions.pages,
        action,
      );

      const disclosures = action.payload._embedded?.disclosures;

      if (disclosures) {
        // Update disclosures in base question object.
        state.questions.pages = updateBaseQuestionDisclosures(
          state.questions.pages,
          action,
        );

        // Update global disclosures.
        state.questions._embedded.disclosures = mergeUpdatedDisclosures(
          disclosures,
          state.questions._embedded.disclosures,
        );
      }
    },
    requestSubmitBaseQuestionFailed: (
      state,
      action: PayloadAction<BaseQuestionError>,
    ) => {
      state.error.baseQuestions = setBaseQuestionError(
        state.error.baseQuestions,
        action.payload,
      );
    },
    requestSubmitDisclosure: (
      state,
      action: PayloadAction<DisclosureRequest>,
    ) => {
      state.error.disclosures = clearDisclosureError(
        state.error.disclosures,
        action.payload.submittedDisclosureIndex,
        action.payload.aliasName,
      );
    },
    requestSubmitDisclosureSuccessful: (
      state,
      action: PayloadAction<EdhConditionDisclosureResource>,
    ) => {
      const updatedDisclosure = [action.payload];

      state.questions._embedded.disclosures = mergeUpdatedDisclosures(
        updatedDisclosure,
        state.questions._embedded.disclosures,
      );
    },
    requestSubmitDisclosureFailed: (
      state,
      action: PayloadAction<DisclosureErrorPayload>,
    ) => {
      state.error.disclosures = setDisclosureError(
        state.error.disclosures,
        action.payload,
      );
    },
    requestDeleteDisclosure: (
      state,
      action: PayloadAction<DeleteDisclosureRequest>,
    ) => {
      state.error.baseQuestions = clearBaseQuestionError(
        state.error.baseQuestions,
        action.payload.baseQuestionCode,
      );
    },
    requestDeleteDisclosureSuccessful: (
      state,
      action: PayloadAction<{
        disclosureName: string;
        baseQuestionCode: string;
      }>,
    ) => {
      const { baseQuestionCode, disclosureName } = action.payload;

      state.questions.pages = removeDisclosureFromBaseQuestionByAliasName(
        baseQuestionCode,
        disclosureName,
        state.questions.pages,
      );

      state.questions._embedded.disclosures =
        removeEmbeddedDisclosureByAliasName(
          action.payload.disclosureName,
          state.questions._embedded.disclosures,
        );
    },
    requestDeleteDisclosureFailed: (
      state,
      action: PayloadAction<BaseQuestionError>,
    ) => {
      state.error.baseQuestions = setBaseQuestionError(
        state.error.baseQuestions,
        action.payload,
      );
    },
    setCurrentInterviewPage: (state, action: PayloadAction<string>) => {
      state.current = updatePageAndFirstSectionByPageCode(
        state.questions.pages,
        action.payload,
        state.questions._embedded.disclosures,
      );

      state.metadata.orderedSectionIndex = getOrderedSectionIndex(
        state.current.section.code,
        state.questions.pages,
      );
    },
    setCurrentInterviewSection: (state, action: PayloadAction<string>) => {
      state.current.section.code = action.payload;

      const sections = getSectionsByPageCode(
        state.questions.pages,
        state.current.page.code,
      );

      if (sections) {
        state.current.section.index = getIndexOfSection(
          sections,
          action.payload,
        );
      }

      state.metadata.orderedSectionIndex = getOrderedSectionIndex(
        action.payload,
        state.questions.pages,
      );
    },
    navigateToNextInterviewSection: (state) => {
      state.current = getNextSectionForCurrent(
        state.questions.pages,
        state.current,
      );

      state.metadata.orderedSectionIndex = getOrderedSectionIndex(
        state.current.section.code,
        state.questions.pages,
      );
    },
    navigateToPreviousInterviewSection: (state) => {
      state.current = getPreviousSectionForCurrent(
        state.questions.pages,
        state.current,
      );

      state.metadata.orderedSectionIndex = getOrderedSectionIndex(
        state.current.section.code,
        state.questions.pages,
      );
    },
    navigateToFirstPageAndSection: (state) => {
      state.current = findLastActivePageAndSection(
        state.questions.pages,
        state.current,
        state.questions._embedded.disclosures,
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      physicianActions.requestCreatePrimaryPhysicianSuccessful,
      (state, action) => {
        // Add physician to list of all physicians.
        if (state.questions._embedded.physicians) {
          state.questions._embedded.physicians?.unshift(action.payload);
        } else {
          state.questions._embedded.physicians = [action.payload];
        }

        // Update primary physician in state.
        if (action.payload.uuid) {
          state.questions._embedded.primaryPhysician =
            mapPhysicianResourceToEdhPrimaryPhysicianResource(
              state.questions.caseId,
              action.payload,
            );
        }
      },
    );
    builder.addCase(
      physicianActions.requestUpdatePrimaryPhysicianSuccessful,
      (state, action) => {
        if (state.questions._embedded.physicians) {
          state.questions._embedded.physicians[0] = action.payload;
        }

        if (action.payload.uuid) {
          state.questions._embedded.primaryPhysician =
            mapPhysicianResourceToEdhPrimaryPhysicianResource(
              state.questions.caseId,
              action.payload,
            );
        }
      },
    );
    builder.addCase(
      physicianActions.requestCreateDisclosurePhysicianSuccessful,
      (state, action) => {
        const { disclosurePhysician, physicianResource } = action.payload;

        if (physicianResource) {
          const { uuid } = disclosurePhysician.physicianIdentifier;

          if (state.questions._embedded.physicians) {
            state.questions._embedded.physicians.push({
              ...physicianResource,
              uuid,
            });
          } else {
            state.questions._embedded.physicians = [
              {
                ...physicianResource,
                uuid,
              },
            ];
          }
        }

        // Add newly created disclosure physician to embedded disclosure physicians.
        state.questions._embedded.disclosure_physicians =
          updateDislosurePhysicianList(
            state.questions._embedded.disclosure_physicians,
            disclosurePhysician,
          );

        // Merge updated disclosure after deletion and recreation.
        if (action.payload.updatedDisclosure) {
          state.questions._embedded.disclosures = mergeUpdatedDisclosures(
            [action.payload.updatedDisclosure],
            state.questions._embedded.disclosures,
          );
        }
      },
    );
    builder.addCase(
      physicianActions.requestUpdateDisclosurePhysicianSuccessful,
      (state, action) => {
        // TODO: Merge updated physician into state.
      },
    );
    builder.addCase(requestSingleCaseSuccessful, (state, action) => {
      // Extract risk types when case received.
      // Required to get correct title in custom physician section.
      const { insurances } = action.payload;
      const risks = insurances.map((i) => i.risks);

      state.metadata.risks = risks[0];
    });
  },
});

export const { actions, reducer } = interviewSlice;
export const {
  navigateToFirstPageAndSection,
  navigateToNextInterviewSection,
  navigateToPreviousInterviewSection,
  requestInterview,
  requestStartInterview,
  requestDeleteDisclosure,
  requestSubmitBaseQuestion,
  requestSubmitDisclosure,
  setCurrentInterviewPage,
  setCurrentInterviewSection,
} = actions;
export { initialState };
export default reducer;
